/* Copyright (c) 2021-2022 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { ComponentProps, ComponentType, PropsWithChildren } from "react";
import { ErrorBoundary, FallbackProps } from "react-error-boundary";
import { If, Then, Else } from "react-if";
import { noop } from "~/tools";
import { ErrorMessage } from "./ErrorMessage";
import { DefaultErrorFallback } from "./Fallback";

type ErrorMsgProps = ComponentProps<typeof ErrorMessage>;

type Props = ErrorMsgProps &
  PropsWithChildren<{
    Component?: ComponentType<ErrorMsgProps>;
    fallback?: ComponentType<FallbackProps>;
    onReset?: ComponentProps<typeof ErrorBoundary>["onReset"];
    resetKeys?: ComponentProps<typeof ErrorBoundary>["resetKeys"];
  }>;

const defaultResetKeys: unknown[] = [];

export function ErrorWrap({
  Component = DefaultComponent,
  fallback = DefaultErrorFallback,
  onReset = noop,
  resetKeys = defaultResetKeys,
  ...props
}: Props) {
  return (
    <ErrorBoundary
      FallbackComponent={fallback}
      onReset={onReset}
      resetKeys={resetKeys}
    >
      <If condition={props.error != null}>
        <Then>
          <Component {...props} />
        </Then>
        <Else>{props.children}</Else>
      </If>
    </ErrorBoundary>
  );
}

type DefaultComponentProps = Omit<Props, "Component" | "fallback" | "onReset">;

function DefaultComponent({ children, ...props }: DefaultComponentProps) {
  return (
    <>
      <ErrorMessage {...props} />
      {children}
    </>
  );
}
